/**
 * # Allowlist Registry Design
 *
 * This file defines the complete list of surface IDs for each surface type
 * (features, top-bar, panels, entry-points).
 *
 * The workspace registry then picks a SUBSET of each items
 * to allow. Everything not in the allowlist is denied by default.
 *
 * ## Surface types and their resolution:
 *
 * - **Feature** — a top-level navigable route (e.g. /messages). Checked via path
 *   resolution from a URL pathname.
 * - **TopBarSurfaceId** — a concrete top-bar UI element (button or panel).
 *   Components pass their own surface ID to the hook.
 * - **GlobalPanelSurfaceId** — a slide-in detail panel.
 *   Components pass their own surface ID to the hook.
 * - **EntryPointSurfaceId** — a dashboard tile, quick-action button, or shortlink.
 *   Components pass their own surface ID to the hook.
 *
 * ## Adding a new feature or surface:
 *   1. Add it to the relevant array below (type auto-updates)
 *   2. If it should be allowed in workspace: add to WORKSPACE_ALLOWLIST in registry.ts
 */

// ─────────────────────────────────────────────────────────────────────────────
// Features — the 11 primary navigable routes
// ─────────────────────────────────────────────────────────────────────────────

/**
 * All known feature route segments. Each corresponds to a top-level URL path
 * (e.g. 'messages' → /messages/*). 'home' is the root '/' path.
 *
 * This array is the single source of truth:
 * - The Feature type is derived from it (no manual union)
 * - FEATURE_SET is derived from it (for O(1) membership checks)
 * - Path resolution uses FEATURE_SET to distinguish "known feature" from
 *   "not our business" (auth pages, onboarding, etc.)
 */
export const FEATURES = [
  'home',
  'messages',
  'calls',
  'schedule',
  'payments',
  'forms',
  'fax',
  'contacts',
  'reviews',
  'analytics',
  'marketing',
] as const;

export type Feature = (typeof FEATURES)[number];

/** Runtime set for O(1) feature membership checks in path resolution. */
export const FEATURE_SET: ReadonlySet<string> = new Set(FEATURES);

// ─────────────────────────────────────────────────────────────────────────────
// ID model — capabilities and surfaces (deliberately separate)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Panels that are their own standalone capability — they have a direct entry in
 * WORKSPACE_ALLOWLIST and can be independently allowed or blocked.
 *
 * Contrast with dependent panels (call-insights, voicemail-details, message-insights)
 * which are surfaces only: they inherit their decision from a parent Feature
 * via PANEL_PARENTS and never appear in the allowlist directly.
 */
export const INDEPENDENT_PANEL_SURFACES = ['team-chat', 'task-center'] as const;

export type IndependentPanelId = (typeof INDEPENDENT_PANEL_SURFACES)[number];

/**
 * CapabilityId — what goes in the allowlist.
 *
 * Intentionally does NOT include all GlobalPanelSurfaceIds:
 * only Features (route-based) and IndependentPanelIds (standalone panels)
 * can appear in the allowlist. Dependent panels are surfaces, not capabilities.
 */
export type CapabilityId = Feature | IndependentPanelId;

// ─────────────────────────────────────────────────────────────────────────────
// Top-bar surfaces — persistent widgets in the app shell
// ─────────────────────────────────────────────────────────────────────────────

/**
 * All known top-bar UI surfaces. A surface identifies a concrete UI element —
 * e.g. 'quickfill-button' and 'quickfill-panel' are two surfaces for the same
 * feature, so they can share one allowlist entry or be listed independently.
 */
export const TOP_BAR_SURFACES = [
  'team-chat-button',
  'dialpad-button',
  'quickfill-button',
  'quickfill-panel',
  'schedule-pulse-button',
  'schedule-pulse-panel',
  'task-center-button',
] as const;

export type TopBarSurfaceId = (typeof TOP_BAR_SURFACES)[number];

// ─────────────────────────────────────────────────────────────────────────────
// Global panel surfaces — slide-in detail panels
// ─────────────────────────────────────────────────────────────────────────────

/**
 * All known global panel surfaces. These are slide-in panels using PanelPortal,
 * triggered from various contexts (inbox threads, notifications, top-bar buttons).
 *
 * Two sub-categories:
 * - IndependentPanelId (team-chat, task-center): own state, own allowlist entry
 * - Dependent panels (call-insights, voicemail-details, message-insights):
 *   inherit their decision from a parent Feature via PANEL_PARENTS in registry.ts
 */
export const GLOBAL_PANEL_SURFACES = [
  ...INDEPENDENT_PANEL_SURFACES,
  'call-insights',
  'voicemail-details',
  'message-insights',
] as const;

export type GlobalPanelSurfaceId = (typeof GLOBAL_PANEL_SURFACES)[number];

// ─────────────────────────────────────────────────────────────────────────────
// Entry-point surfaces — dashboard tiles, quick actions, shortlinks
// ─────────────────────────────────────────────────────────────────────────────

/**
 * All known entry-point UI surfaces. Three categories:
 *
 * dashboard.*    — tiles/cards on the home dashboard
 * quick-action.* — buttons in the floating action bar
 * shortlink.*    — deep-link panels opened from notifications
 *
 * Entry points are gated separately from routes because hiding/disabling a
 * button is a different UX concern from blocking navigation.
 */
export const ENTRY_POINT_SURFACES = [
  'dashboard.home',
  'dashboard.calls',
  'dashboard.voicemails',
  'dashboard.messages',
  'dashboard.contacts',
  'dashboard.lists',
  'dashboard.schedule.route',
  'dashboard.schedule.pulse',
  'dashboard.forms',
  'dashboard.reviews',
  'dashboard.analytics',
  'dashboard.payments',
  'dashboard.fax',
  'dashboard.team-chat',
  'dashboard.task-center',
  'dashboard.quickfill',
  'dashboard.marketing',
  'quick-action.new-message',
  'quick-action.request-payment',
  'quick-action.new-appointment',
  'quick-action.new-out-of-office',
  'quick-action.new-fax',
  'quick-action.new-review-request',
  'shortlink.voicemail-details',
  'shortlink.call-insights',
] as const;

export type EntryPointSurfaceId = (typeof ENTRY_POINT_SURFACES)[number];

// ─────────────────────────────────────────────────────────────────────────────
// Banner surfaces — global info banners shown at the top of the app
// ─────────────────────────────────────────────────────────────────────────────

/**
 * All known global banner surfaces. Each corresponds to a specific banner
 * component that registers itself via GlobalBannerDynamic.
 *
 * Banners inherit their workspace access from a parent CapabilityId via
 * BANNER_PARENTS in registry.ts — same pattern as other surface types.
 */
export const BANNER_SURFACES = [
  'banner.messaging-compliance',
  'banner.domain-verify',
  'banner.call-recordings',
  'banner.call-intel-trial',
  'banner.call-intel-backfill',
  'banner.call-intel-base-backfill',
  'banner.sync-app-auth',
  'banner.wie-connect-payers',
  'banner.self-upgrade',
  'banner.billing-alert',
  'banner.phone-override',
  'banner.forms-freemium',
  'banner.notifications-disabled',
  'banner.softphone-tab',
  'banner.announcement',
] as const;

export type BannerSurfaceId = (typeof BANNER_SURFACES)[number];

/**
 * A SurfaceId identifies any concrete UI surface that can be checked for access
 * in the scope policy hook.
 */
export type SurfaceId = TopBarSurfaceId | GlobalPanelSurfaceId | EntryPointSurfaceId | BannerSurfaceId;
